import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import type { BuiltLocation, Navigation } from 'modules/core/lib/navigation/context'
import type { NavigationPaths } from 'modules/core/lib/navigation/context'

function splitPath(pathname: string) {
  return pathname
    .replace(/\/\//g, '/') // Remove double /
    .replace(/^\/|\/$/g, '') // Remove / from beginning and end
    .split('/')
}

function getParamsIndexes(parts: string[]) {
  const paramsIndexes: number[] = []

  parts.forEach((part, index) => part[0] === ':' && paramsIndexes.push(index))

  return paramsIndexes
}

function getFullPath(path: string, prevPath: string) {
  return prevPath === '' || prevPath === '/' ? path : `${prevPath}/${path}`
}

export const matchPath = (rawPath: string, rawPathname: string) => {
  const pathnameParts = splitPath(rawPathname)
  const pathParts = splitPath(rawPath)
  const paramsIndexes = getParamsIndexes(pathParts)

  let weight = 0

  const matches = pathParts.every((part, index) => {
    if (paramsIndexes.includes(index) && pathnameParts[index]?.length) {
      weight += 0.5

      return true
    }

    if (part === pathnameParts[index]) {
      weight++

      return true
    }

    return false
  })

  return { matches, weight }
}

type MatchArray<T extends Navigation<NavigationPaths>, U extends string> = Array<{
  weight: number
  child: BuiltLocation<T, U>
}>

const matchChildren = <T extends Navigation<NavigationPaths>, U extends string>(
  pathname: string,
  navigation: BuiltLocation<T, U>,
  prevPath = ''
): MatchArray<T, U> => {
  const children = Object.values(navigation)
  const candidates: MatchArray<T, U> = []

  children.forEach((element) => {
    if (typeof element !== 'object') return

    const child = element as BuiltLocation<T, U>
    const { matches, weight } = matchPath(getFullPath(child.path, prevPath), pathname)

    if (!matches) return

    candidates.push({ weight, child })
  })

  return candidates
}

export const matchArray = <T extends Navigation<NavigationPaths>, U extends string>(
  pathname: string,
  navigation: BuiltLocation<T, U>,
  result: MatchArray<T, U> = [{ weight: 0, child: navigation }],
  prevPath = ''
): MatchArray<T, U> => {
  const fullPath = getFullPath(navigation.path, prevPath)
  const matchingChildren = matchChildren(pathname, navigation, fullPath)

  if (matchingChildren.length) {
    result = [...result, ...matchingChildren]

    matchingChildren.forEach((element) => {
      result = matchArray(pathname, element.child, result, fullPath)
    })
  }

  return result
}

const useNavigationMatch = (navigation: Navigation<NavigationPaths>, pathname: string) => {
  return useUnsafeMemo(() => {
    const result = matchArray<Navigation<NavigationPaths>, Navigation<NavigationPaths>['path']>(
      pathname,
      navigation
    )

    return result.sort((a, b) => a.weight - b.weight).map(({ child }) => child)
  }, [pathname, navigation])
}

export default useNavigationMatch
